import React, { Children } from 'react';
import { X, Brain, Waves, TreePine, Fish, Cpu, Eye, Zap, Activity, Database, Network } from 'lucide-react';
import { motion } from 'framer-motion';
interface Props {
  onClose: () => void;
}
const TRAINING_FACTORS = [{
  id: '01',
  title: 'Environmental Cues & Decision-Making',
  icon: <Brain size={20} className="text-cyan-400" data-id="element-75" />,
  points: ['Sensory inputs: sight, smell, lateral line, electroreception', 'Water chemistry influence on perception and feeding', 'Species-specific sensitivities to environmental changes'],
  status: 92
}, {
  id: '02',
  title: 'Habitat Features & Attraction',
  icon: <TreePine size={20} className="text-emerald-400" data-id="element-76" />,
  points: ['Physical structures: rocks, vegetation, substrate types', 'Chemical signals: pheromones, dissolved organic compounds', 'Light gradients and shadow effects on fish movement'],
  status: 88
}, {
  id: '03',
  title: 'Dynamic Environments & Adaptations',
  icon: <Waves size={20} className="text-blue-400" data-id="element-77" />,
  points: ['Seasonal fluctuations in temperature and food availability', 'Impact of human activity and habitat modification', 'Adaptive strategies fish employ to navigate changes'],
  status: 76
}, {
  id: '04',
  title: 'Complexity & Social Behavior',
  icon: <Fish size={20} className="text-purple-400" data-id="element-78" />,
  points: ['How diverse habitats facilitate social aggregation', 'Relationship between environmental richness and feeding', 'Implications for conservation and habitat restoration'],
  status: 85
}, {
  id: '05',
  title: 'Modern Sensing Technologies',
  icon: <Cpu size={20} className="text-pink-400" data-id="element-79" />,
  points: ['Underwater imaging and sensor technology integration', 'Simulation models predicting fish response', 'Real-time habitat analysis and data processing'],
  status: 95
}, {
  id: '06',
  title: 'Artificial Environment Design',
  icon: <Database size={20} className="text-yellow-400" data-id="element-80" />,
  points: ['Principles for creating artificial habitats', 'Environmental manipulation for sustainable harvests', 'Ethical considerations and ecological impacts'],
  status: 70
}, {
  id: '07',
  title: 'Integrated Attraction Science',
  icon: <Network size={20} className="text-indigo-400" data-id="element-81" />,
  points: ['Bridging environmental factors with behavioral studies', 'Predictive modeling for lure presentation', 'Comprehensive AI training for adaptive algorithms'],
  status: 82
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
export function AIProgramPage({
  onClose
}: Props) {
  return <div className="absolute inset-0 z-50 bg-[#050b14] flex flex-col overflow-hidden font-sans" data-id="element-82">
      {/* High-tech background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-83">
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(0,212,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,212,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px]" data-id="element-84" />
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-cyan-500/10 blur-[120px] rounded-full" data-id="element-85" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-blue-600/10 blur-[150px] rounded-full" data-id="element-86" />
      </div>

      {/* Header */}
      <div className="h-16 border-b border-cyan-500/20 flex items-center justify-between px-5 shrink-0 bg-[#0a1628]/80 backdrop-blur-md relative z-10" data-id="element-87">
        <div className="flex items-center gap-3" data-id="element-88">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30" data-id="element-89">
            <Activity size={16} className="text-cyan-400" data-id="element-90" />
            <span className="absolute top-0 right-0 w-2 h-2 bg-cyan-400 rounded-full animate-ping" data-id="element-91" />
          </div>
          <div data-id="element-92">
            <h2 className="text-sm font-bold text-white tracking-wide uppercase" data-id="element-93">
              AI Training Intelligence
            </h2>
            <p className="text-[10px] text-cyan-400/70 tracking-widest uppercase" data-id="element-94">
              BaitMover Pro Neural Core
            </p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-cyan-500/20 text-white/70 hover:text-cyan-300 transition-all border border-transparent hover:border-cyan-500/30" data-id="element-95">
          <X size={18} data-id="element-96" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 relative z-10 custom-scrollbar" data-id="element-97">
        {/* Intro Section */}
        <div className="mb-8 relative" data-id="element-98">
          <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-cyan-400 to-blue-600 rounded-full" data-id="element-99" />
          <div className="pl-4" data-id="element-100">
            <h3 className="text-lg font-semibold text-white mb-2" data-id="element-101">
              Adaptive Algorithmic Training
            </h3>
            <p className="text-xs text-white/60 leading-relaxed" data-id="element-102">
              The BaitMover Pro AI engine is currently ingesting vast datasets
              across multiple ecological and behavioral vectors. By analyzing
              these complex environmental interactions, the system will soon be
              capable of autonomously generating optimal bobber movements
              tailored to real-time conditions.
            </p>
          </div>
        </div>

        {/* Data Modules Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid gap-4 pb-6" data-id="element-103">
          {TRAINING_FACTORS.map(factor => <motion.div key={factor.id} variants={itemVariants} className="relative p-4 rounded-xl bg-[#0a1628]/60 border border-white/5 overflow-hidden group hover:border-cyan-500/30 transition-colors" data-id="element-104">
              {/* Scanline effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/5 to-transparent h-[200%] -top-[100%] group-hover:animate-[scan_2s_linear_infinite] pointer-events-none opacity-0 group-hover:opacity-100" data-id="element-105" />

              <div className="flex items-start gap-4 relative z-10" data-id="element-106">
                <div className="shrink-0 mt-1" data-id="element-107">
                  <div className="w-10 h-10 rounded-lg bg-[#050b14] border border-white/10 flex items-center justify-center shadow-[inset_0_0_15px_rgba(0,0,0,0.5)] group-hover:border-cyan-500/40 group-hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all" data-id="element-108">
                    {factor.icon}
                  </div>
                  <div className="text-[9px] text-center mt-1.5 text-white/30 font-mono tracking-wider" data-id="element-109">
                    MOD-{factor.id}
                  </div>
                </div>

                <div className="flex-1" data-id="element-110">
                  <div className="flex items-center justify-between mb-2" data-id="element-111">
                    <h4 className="text-sm font-semibold text-white/90 group-hover:text-white transition-colors" data-id="element-112">
                      {factor.title}
                    </h4>
                    <div className="flex items-center gap-1.5" data-id="element-113">
                      <span className="text-[10px] font-mono text-cyan-400/70" data-id="element-114">
                        {factor.status}%
                      </span>
                      <div className="w-12 h-1.5 bg-black/50 rounded-full overflow-hidden border border-white/5" data-id="element-115">
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${factor.status}%`
                    }} transition={{
                      duration: 1.5,
                      delay: 0.5,
                      ease: 'easeOut'
                    }} className="h-full bg-gradient-to-r from-blue-600 to-cyan-400" data-id="element-116" />
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-1.5" data-id="element-117">
                    {factor.points.map((point, idx) => <li key={idx} className="text-[11px] text-white/50 flex items-start gap-2 leading-snug" data-id="element-118">
                        <span className="text-cyan-500/50 mt-0.5 shrink-0" data-id="element-119">
                          ›
                        </span>
                        {point}
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Footer Badge */}
        <div className="mt-4 mb-8 flex justify-center" data-id="element-120">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 shadow-[0_0_20px_rgba(0,212,255,0.1)]" data-id="element-121">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" data-id="element-122" />
            <span className="text-xs font-medium text-cyan-300 tracking-wide uppercase" data-id="element-123">
              AI Model in Training • Coming Soon
            </span>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes scan {
          0% { transform: translateY(0); }
          100% { transform: translateY(50%); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.2);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 212, 255, 0.2);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 212, 255, 0.4);
        }
      `
    }} data-id="element-124" />
    </div>;
}